/**
 * Utility functions for band-related operations
 */
import { englishT } from '../locales/index.js';

/**
 * Check if a user is a member of a band
 * @param {Object} bandInfo - The band information object containing Members array
 * @param {string} userId - The user ID to check for membership
 * @returns {boolean} - True if user is a member of the band, false otherwise
 */
export const isBandMember = (bandInfo, userId) => {
  const members = bandInfo?.Members ?? bandInfo?.members;
  if (!Array.isArray(members) || !userId) {
    return false;
  }
  
  return members.some(member =>
    (member.UserID ?? member.user_id ?? member.userId)?.toString() === userId.toString()
  );
};

/**
 * Get the current user's ID from session storage
 * @returns {string|null} - The user ID or null if not logged in
 */
export const getCurrentUserId = () => {
  return sessionStorage.getItem('userId');
};

/**
 * Check if a user is logged in
 * @returns {boolean} - True if user is logged in, false otherwise
 */
export const isUserLoggedIn = () => {
  return !!getCurrentUserId();
};

/**
 * Whether a band can take tips: the one place that decides whether a tip
 * button, the watch page's tip bar or Hype is offered (ENG-496).
 *
 * It reads `hasPaymentProvider` from `GET /public/bands/{id}`. Since
 * ENG-572 that is true only when the band has Stripe connected, because the
 * public tip checkout (`POST /public/bands/{id}/tips/checkout`) is Stripe
 * only. A band with only PayPal connected gets false, so tipping is hidden
 * for it (a PayPal tip path is planned post-Alpha, ENG-583). The response's
 * `paymentProviders` lists every connected provider by name (e.g.
 * `["paypal","stripe"]`) but is not what decides this. The member-only
 * `GET /bands/{id}` carries no payment fields at all, so a decision made
 * from it (or from the individual method fields) hid tipping for every band.
 * TipModal still picks among the concrete methods it is given, but only
 * once this says tips are possible.
 * @param {Object|null} publicBandInfo - the `/public/bands/{id}` response
 * @returns {boolean}
 */
export const canBandTakeTips = (publicBandInfo) => {
  const value = publicBandInfo?.hasPaymentProvider ?? publicBandInfo?.has_payment_provider;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return value === true;
};

/** Catalog keys of the donation method labels ("Card (Stripe)", "PayPal", …). */
const METHOD_LABEL_KEYS = {
  stripe: 'payment.methods.stripe',
  paypal: 'payment.methods.paypal',
  venmo: 'payment.methods.venmo',
  cashapp: 'payment.methods.cashapp',
  zelle: 'payment.methods.zelle',
};

/**
 * Build the list of available donation methods from a band info object.
 *
 * Unreachable in production for now: no backend response carries the fields
 * read here (stripe_link, stripe_account_id, paypal_link, paypal_email,
 * venmo_*, cash_app_* / cashapp_*, zelle_*). `GET /public/bands/{id}` sends
 * only `hasPaymentProvider` and provider names in `paymentProviders`
 * (ENG-572), so against the real API this returns [] and TipModal goes
 * straight to Stripe Checkout. The per-method paths are kept for the planned
 * PayPal tip path (ENG-583) and are exercised only by unit tests and e2e stubs.
 * Returns methods in a consistent order: stripe, paypal, venmo, cashapp, zelle.
 * `label` is worded in the locale of `t` (English without one); `labelKey`
 * lets a component translate it itself (ENG-386).
 * @param {Object|null} bandInfo
 * @param {(key: string) => string} [t]
 * @returns {Array<{key: string, label: string, labelKey: string, value: string|boolean}>}
 */
export const getDonationMethods = (bandInfo, t = englishT) => {
  if (!bandInfo || typeof bandInfo !== 'object') return [];
  const methods = [
    {
      key: 'stripe',
      value: bandInfo.stripe_link || bandInfo.stripeLink || bandInfo.stripe_account_id || bandInfo.stripeAccountId,
    },
    {
      key: 'paypal',
      value: bandInfo.paypal_link || bandInfo.paypalLink || bandInfo.paypal_email || bandInfo.paypalEmail,
    },
    {
      key: 'venmo',
      value: bandInfo.venmo_link || bandInfo.venmoLink || bandInfo.venmo_username || bandInfo.venmoUsername,
    },
    {
      key: 'cashapp',
      value: bandInfo.cash_app_link || bandInfo.cashAppLink || bandInfo.cashapp_handle || bandInfo.cashappHandle,
    },
    {
      key: 'zelle',
      value: bandInfo.zelle_email || bandInfo.zelleEmail,
    },
  ].map((m) => ({ key: m.key, label: t(METHOD_LABEL_KEYS[m.key]), labelKey: METHOD_LABEL_KEYS[m.key], value: m.value }));
  return methods.filter((m) => {
    if (typeof m.value === 'string') return m.value.trim().length > 0;
    return Boolean(m.value);
  });
};

/**
 * Resolve a band's Twitch channel name from an explicit field or a twitch.tv
 * social media link.
 * @param {Object|null} band
 * @returns {string|null}
 */
export const extractTwitchChannel = (band) => {
  if (!band) return null;
  const direct = band.TwitchChannel || band.twitch_channel || band.twitchChannel;
  if (direct) return direct;
  if (Array.isArray(band.SocialMediaLinks)) {
    for (const link of band.SocialMediaLinks) {
      try {
        const raw = String(link);
        const withProtocol = raw.includes('://') ? raw : `https://${raw}`;
        const url = new URL(withProtocol);
        const hostname = url.hostname.toLowerCase();
        if (hostname !== 'twitch.tv' && hostname !== 'www.twitch.tv') continue;
        const segments = url.pathname.split('/').filter(Boolean);
        if (segments.length === 1 && /^[a-zA-Z0-9_]+$/.test(segments[0])) {
          return segments[0];
        }
      } catch {
        // not a valid URL
      }
    }
  }
  return null;
};
